import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';

import { bem } from 'helpers/class-name';
import { getCrashErrorAttributes } from 'helpers/crash-attributes';
import DatadogRum, { DATADOG_RUM_ACTIONS } from 'helpers/datadog-rum';
import { trackCrashedPageViewed } from 'helpers/events';
import { getFailedRequestsForAttribution } from 'helpers/http';
import { getDecagonChatPath } from 'helpers/link-generation';
import Message from 'components/Message';
import { selectActiveVenueId } from 'selectors/general';

import messages from './InternalError.messages';
import './InternalError.scss';

const { block, element } = bem('internal-error');

function getCrashType(crashError, lastFailed) {
  if (crashError) return 'js_error';
  if (lastFailed) return 'api_failure';
  return 'unknown';
}

const InternalError = ({ errorCode: errorCodeProp, crashError }) => {
  const { pathname, search } = useLocation();
  const venueId = useSelector(selectActiveVenueId);
  // Guard against reporting the same crash more than once. This effect also
  // depends on `venueId`, which can populate asynchronously after mount and
  // would otherwise re-fire the action for a single crashed page view.
  const reportedPathRef = useRef(null);

  useEffect(() => {
    if (reportedPathRef.current === pathname) return;
    reportedPathRef.current = pathname;

    const allFailedRequests = getFailedRequestsForAttribution();
    const lastFailed = allFailedRequests.length ? allFailedRequests[allFailedRequests.length - 1] : null;
    const errorCode = errorCodeProp ?? lastFailed?.statusCode;

    trackCrashedPageViewed({ venueId, errorCode });
    DatadogRum.addAction(DATADOG_RUM_ACTIONS.CRASHED_PAGE_VIEWED, {
      urlPath: pathname,
      crashType: getCrashType(crashError, lastFailed),
      failedEndpoints: allFailedRequests.map(r => r.apiEndpoint).join(', '),
      failedStatusCodes: allFailedRequests.map(r => r.statusCode).join(', '),
      apiEndpoint: lastFailed?.apiEndpoint,
      statusCode: lastFailed?.statusCode,
      method: lastFailed?.method,
      ...getCrashErrorAttributes(crashError),
    });
  }, [crashError, errorCodeProp, pathname, venueId]);

  return (
    <div className={block()}>
      <div className={element('content')}>
        <Message as="p" className="text--bold" message={messages.title} />

        <Message
          as="p"
          message={messages.body}
          values={{
            a: text => <Link to={getDecagonChatPath(pathname, search)}>{text}</Link>,
          }}
        />
      </div>
    </div>
  );
};

InternalError.propTypes = {
  errorCode: PropTypes.number,
  // JS lets you throw anything — numbers, strings, null-prototype objects.
  // eslint-disable-next-line react/forbid-prop-types
  crashError: PropTypes.any,
};

export default InternalError;
